const STOREFRONT_TOKEN = '2937d6f3fe5a45d3a3d1da3fc5a7050c';
const STOREFRONT_API_VERSION = '2024-10';

function formatMoney(cents) {
  const dollars = cents / 100;
  return '$' + (Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2));
}

class AutoDiscountPrice {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.cache = new Map();
    this.init();
  }

  get priceContainer() {
    return document.getElementById(`price-${this.sectionId}`);
  }

  init() {
    const variantId = this.getVariantId();
    if (variantId) this.check(variantId);
    this.setupObservers();
  }

  getVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return parseInt(fromUrl);
    const productEl = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
    const input = productEl?.querySelector('input[name="id"]');
    return input ? parseInt(input.value) : null;
  }

  setupObservers() {
    // Price block: Dawn updates via innerHTML swap — element stays, children change.
    const priceContainer = this.priceContainer;
    if (priceContainer) {
      new MutationObserver(() => {
        const variantId = this.getVariantId();
        if (variantId) this.check(variantId);
      }).observe(priceContainer, { childList: true });
    }

    // Variant picker: Dawn uses HTMLUpdateUtility.viewTransition which inserts a NEW
    // variant-selects element before the old one, then removes the old after 500ms.
    // Observe the parent so we catch each new element being inserted.
    const picker = document.getElementById(`variant-selects-${this.sectionId}`);
    if (picker?.parentElement) {
      new MutationObserver(() => {
        const variantId = this.getVariantId();
        if (variantId) this.reapplyPills(variantId);
      }).observe(picker.parentElement, { childList: true });
    }
  }

  async check(variantId) {
    const discount = await this.fetchDiscount(variantId);
    this.applyDisplay(discount);
    if (discount) this.applyVariantPills(discount.original - discount.final);
  }

  async reapplyPills(variantId) {
    const discount = await this.fetchDiscount(variantId);
    if (discount) this.applyVariantPills(discount.original - discount.final);
  }

  async fetchDiscount(variantId) {
    if (this.cache.has(variantId)) return this.cache.get(variantId);

    const shop = window.Shopify?.shop;
    if (!shop) return null;

    try {
      const res = await fetch(
        `https://${shop}/api/${STOREFRONT_API_VERSION}/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: `mutation {
              cartCreate(input: {
                lines: [{ quantity: 1, merchandiseId: "gid://shopify/ProductVariant/${variantId}" }]
              }) {
                cart {
                  lines(first: 1) {
                    edges {
                      node {
                        cost {
                          subtotalAmount { amount }
                          totalAmount { amount }
                        }
                      }
                    }
                  }
                }
              }
            }`,
          }),
        }
      );

      const json = await res.json();
      const node = json?.data?.cartCreate?.cart?.lines?.edges?.[0]?.node;

      if (!node) {
        this.cache.set(variantId, null);
        return null;
      }

      const original = Math.round(parseFloat(node.cost.subtotalAmount.amount) * 100);
      const final = Math.round(parseFloat(node.cost.totalAmount.amount) * 100);
      const result = final < original ? { original, final } : null;
      this.cache.set(variantId, result);
      return result;
    } catch {
      this.cache.set(variantId, null);
      return null;
    }
  }

  applyDisplay(discount) {
    const container = this.priceContainer;
    if (!container) return;
    const priceDiv = container.querySelector('.price');
    if (!priceDiv) return;

    if (discount) {
      const strikeEl = priceDiv.querySelector('.price__sale .price-item--regular');
      const saleEl = priceDiv.querySelector('.price-item--sale');
      if (strikeEl) strikeEl.textContent = formatMoney(discount.original);
      if (saleEl) saleEl.textContent = formatMoney(discount.final);
      priceDiv.classList.add('price--on-sale', 'price--has-auto-discount');
    } else if (priceDiv.classList.contains('price--has-auto-discount')) {
      priceDiv.classList.remove('price--on-sale', 'price--has-auto-discount');
    }
  }

  applyVariantPills(discountAmount) {
    document.querySelectorAll('.product-variant-price').forEach((pill) => {
      // Skip pills inside the old hidden picker (viewTransition keeps it for 500ms)
      if (!pill.offsetParent) return;
      // Use stored original if we've seen this pill before; fresh textContent after re-render
      const original = pill.dataset.originalPrice || pill.textContent.trim();
      if (!original.startsWith('$')) return;
      const originalCents = Math.round(parseFloat(original.slice(1)) * 100);
      if (!originalCents) return;
      const finalCents = Math.max(0, originalCents - discountAmount);
      pill.dataset.originalPrice = original;
      pill.innerHTML = `<span class="product-variant-price-original">${original}</span> ${formatMoney(finalCents)}`;
    });
  }
}

// Watch a parent element for new product-info or main elements being inserted
// (covers both full-page and partial product swaps via HTMLUpdateUtility.viewTransition).
function watchForSwap(parent) {
  if (!parent) return;
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const candidates = node.matches('product-info[data-section]')
          ? [node]
          : [...node.querySelectorAll('product-info[data-section]')];
        candidates.forEach((el) => new AutoDiscountPrice(el.dataset.section));
      }
    }
  }).observe(parent, { childList: true });
}

document.querySelectorAll('product-info[data-section]').forEach((el) => {
  new AutoDiscountPrice(el.dataset.section);
});

// Full swap: Dawn replaces <main> when dropdown links to a different product URL
watchForSwap(document.querySelector('main')?.parentElement);

// Partial swap: Dawn replaces product-info directly (updateFullPage = false)
document.querySelectorAll('product-info[data-section]').forEach((el) => {
  watchForSwap(el.parentElement);
});
